import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { NavBar } from "@/components/NavBar";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, apiErrorMessage, centsToUsd } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Loader2 } from "lucide-react";

const TIER_ORDER = ["basic", "pro", "enterprise"];

export default function MembershipPage() {
  const navigate = useNavigate();
  const { user, membership, refresh } = useAuth();
  const [config, setConfig] = useState(null);
  const [interval, setInterval] = useState("monthly");
  const [buying, setBuying] = useState("");

  useEffect(() => {
    api
      .get("/config")
      .then(({ data }) => setConfig(data))
      .catch(() => toast.error("Could not load membership catalog."));
  }, []);

  const buy = async (tierKey) => {
    if (!user) {
      toast.info("Sign in or create an account to purchase membership.");
      navigate("/auth?next=/membership");
      return;
    }
    setBuying(tierKey);
    try {
      const { data } = await api.post("/membership/checkout", { tier: tierKey, interval });
      if (data.demo) {
        toast.success("Membership activated (demo mode).");
        await refresh();
        navigate("/dashboard");
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, "Membership checkout failed."));
    } finally {
      setBuying("");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <NavBar />
      <main className="flex-1 mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="text-left max-w-2xl space-y-3">
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Private membership</h1>
          <p className="text-muted-foreground">
            Active membership unlocks the Order Your QSIP workflow. Report requests are billed separately at{" "}
            {config ? centsToUsd(config.reportPriceCents) : "$250"} per verified report.
          </p>
          {config?.demoMode && (
            <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary" data-testid="demo-mode-badge">
              Demo mode — payments are simulated
            </Badge>
          )}
        </div>

        <div className="mt-8">
          <Tabs value={interval} onValueChange={setInterval}>
            <TabsList data-testid="membership-interval-toggle">
              <TabsTrigger value="monthly" data-testid="membership-interval-monthly">Monthly</TabsTrigger>
              <TabsTrigger value="yearly" data-testid="membership-interval-yearly">
                Annual <span className="ml-1.5 text-xs text-[hsl(var(--status-verified))]">2 months free</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {config &&
            TIER_ORDER.map((tierKey, index) => {
              const tier = config.membershipTiers[tierKey];
              if (!tier) return null;
              const amountCents = interval === "monthly" ? tier.monthly_amount_cents : tier.yearly_amount_cents;
              const highlighted = tierKey === "pro";
              const isCurrent = membership?.status === "active" && membership?.tier === tierKey;
              return (
                <motion.div
                  key={tierKey}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.07 }}
                >
                  <Card
                    className={`relative h-full flex flex-col bg-card border-border ${
                      highlighted ? "border-primary/50 shadow-lg shadow-primary/10" : ""
                    }`}
                    data-testid={`membership-tier-${tierKey}-card`}
                  >
                    {highlighted && (
                      <span className="absolute -top-3 left-5 rounded-full border border-primary/40 bg-primary/15 px-3 py-0.5 text-xs font-medium text-primary">
                        Most popular
                      </span>
                    )}
                    <CardHeader>
                      <CardTitle className="font-display text-xl">{tier.name}</CardTitle>
                      <div className="flex items-baseline gap-1.5 pt-1">
                        <span className="font-display text-4xl font-semibold" data-testid={`membership-tier-${tierKey}-price`}>
                          {centsToUsd(amountCents)}
                        </span>
                        <span className="text-sm text-muted-foreground">/{interval === "monthly" ? "month" : "year"}</span>
                      </div>
                    </CardHeader>
                    <CardContent className="flex-1">
                      <ul className="space-y-2.5">
                        {tier.features.map((feature) => (
                          <li key={feature} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                            <Check className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--status-verified))]" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                    <CardFooter>
                      <Button
                        className="w-full"
                        variant={highlighted ? "default" : "secondary"}
                        disabled={buying === tierKey || isCurrent}
                        data-testid={`membership-tier-${tierKey}-buy-button`}
                        onClick={() => buy(tierKey)}
                      >
                        {buying === tierKey ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : isCurrent ? (
                          "Current plan"
                        ) : (
                          `Get ${tier.name}${config.demoMode ? " (Demo)" : ""}`
                        )}
                      </Button>
                    </CardFooter>
                  </Card>
                </motion.div>
              );
            })}
        </div>

        <p className="mt-8 text-xs text-muted-foreground" data-testid="membership-pricing-disclosure">
          Membership fees and report fees are separate products. Reports are{" "}
          {config ? centsToUsd(config.reportPriceCents) : "$250"} each for members — Basic checks out up to 3 at a
          time (10 reports per month), Pro up to 10 (50 per month), and Enterprise up to 25 with unlimited monthly
          reports. Processing by our operations team can take 30–60 days. Report fees are payment for research and
          document-processing services only. Fees are not payment for legal representation, financial advice, or any
          government filing, and do not depend on any particular outcome. Membership renews automatically until
          cancelled; see the Subscription &amp; Automatic Renewal Terms and Refund &amp; Cancellation Policy in the
          footer.
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}
